/**
 * AuthContext - 身份驗證上下文
 * 管理用戶登入狀態、用戶資訊和登入/登出功能
 * 包含閒置超時自動登出功能（30 分鐘）
 */
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const AuthContext = createContext(null);

// 閒置超時時間（毫秒）- 30 分鐘
const IDLE_TIMEOUT = 30 * 60 * 1000;
// 警告時間（毫秒）- 登出前 2 分鐘警告
const WARNING_TIME = 2 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必須在 AuthProvider 內使用');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idleWarning, setIdleWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const navigate = useNavigate();
  
  // 用於追蹤閒置時間的 refs
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // 閒置超時登出
  const handleIdleLogout = useCallback(async () => {
    setIdleWarning(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    try {
      await api.post('/oauth/logout');
    } catch (error) {
      console.error('登出失敗:', error);
    }
    
    setUser(null);
    navigate('/login', { state: { message: '由於閒置時間過長，系統已自動登出' } });
  }, [navigate]);

  // 重置閒置計時器
  const resetIdleTimer = useCallback(() => {
    // 只有在用戶已登入時才追蹤閒置時間
    if (!user) return;
    
    lastActivityRef.current = Date.now();
    setIdleWarning(false);
    
    // 清除現有的計時器
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    // 設置警告計時器（登出前 2 分鐘）
    warningTimerRef.current = setTimeout(() => {
      setIdleWarning(true);
      setRemainingTime(WARNING_TIME / 1000);
      
      // 開始倒數
      countdownRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, IDLE_TIMEOUT - WARNING_TIME);
    
    // 設置登出計時器
    idleTimerRef.current = setTimeout(() => {
      console.log('閒置超時，自動登出');
      handleIdleLogout();
    }, IDLE_TIMEOUT);
  }, [user, handleIdleLogout]);

  // 用戶活動事件
  useEffect(() => {
    if (!user) return;
    
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      // 防止頻繁重置（節流 - 每秒最多重置一次）
      if (Date.now() - lastActivityRef.current > 1000) {
        resetIdleTimer();
      }
    };
    
    // 綁定事件監聯器
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    
    // 初始化計時器
    resetIdleTimer();
    
    // 清理函數
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [user, resetIdleTimer]);

  // 繼續使用（重置閒置時間）
  const stayLoggedIn = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  // 檢查用戶是否已登入
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // 嘗試從 API 獲取當前用戶資訊
      const response = await api.get('/oauth/me');
      if (response.data.authenticated) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      // 401 是正常的未登入狀態,不需要報錯
      if (error.response?.status !== 401) {
        console.error('檢查登入狀態失敗:', error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (provider = 'microsoft') => {
    try {
      // 取得 OAuth 授權 URL (發送空 JSON 物件)
      const response = await api.post(`/oauth/authorize/${provider}`, {});
      const { authorization_url } = response.data;
      
      // 重定向到 OAuth 授權頁面
      window.location.href = authorization_url;
    } catch (error) {
      console.error('登入失敗:', error);
      throw error;
    }
  };

  const logout = async () => {
    // 清除閒置計時器
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    setIdleWarning(false);
    
    try {
      await api.post('/oauth/logout');
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('登出失敗:', error);
      // 即使 API 失敗也清除本地狀態
      setUser(null);
      navigate('/login');
    }
  };

  const handleOAuthCallback = async () => {
    // OAuth callback 後重新檢查登入狀態
    await checkAuthStatus();
    if (user) {
      navigate('/dashboard');
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    checkAuthStatus,
    handleOAuthCallback,
    // 閒置超時相關
    idleWarning,
    remainingTime,
    stayLoggedIn
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* 閒置警告彈窗 */}
      {idleWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">閒置警告</h3>
            </div>
            <p className="text-gray-600 mb-4">
              由於您已閒置一段時間，系統將在 <span className="font-bold text-red-600">{Math.floor(remainingTime / 60)}:{(remainingTime % 60).toString().padStart(2, '0')}</span> 後自動登出。
            </p>
            <p className="text-gray-500 text-sm mb-6">
              點擊「繼續使用」可重新計時。
            </p>
            <div className="flex gap-3">
              <button
                onClick={stayLoggedIn}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                繼續使用
              </button>
              <button
                onClick={logout}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                立即登出
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
